import { ref, watch } from 'vue';
import { KML_LAYER_URL } from '../../constants';

export const useKmlLayer = (map) => {
    const kmlLayer = ref(null);
    const kmlError = ref(null);
    const hasLoggedError = ref(false);

    // Function to add a KML layer to the map
    const addKmlLayer = (kmlUrl) => {
        if (!map.value) {
            kmlError.value = 'Map not initialized yet.';
            return;
        }

        if (!kmlUrl) {
            kmlError.value = 'KML URL is not provided.';
            return;
        }

        try {
            // Create and add the KML Layer
            kmlLayer.value = new google.maps.KmlLayer({
                url: kmlUrl,
                map: map.value,
                suppressInfoWindows: true,
            });

            // Handle KML layer errors
            kmlLayer.value.addListener('status_changed', () => {
                const status = kmlLayer.value.getStatus();

                if (status !== google.maps.KmlLayerStatus.OK) {
                    let errorMessage = 'KML Layer failed to load.';
                    
                    switch (status) {
                        case google.maps.KmlLayerStatus.INVALID_REQUEST:
                            errorMessage = 'Invalid KML request. Please check the KML URL.';
                            break;
                        case google.maps.KmlLayerStatus.DOCUMENT_NOT_FOUND:
                            errorMessage = 'KML file not found. Check the URL.';
                            break;
                        case google.maps.KmlLayerStatus.DOCUMENT_TOO_LARGE:
                            errorMessage = 'KML file is too large to process.';
                            break;
                        case google.maps.KmlLayerStatus.TIMED_OUT:
                            errorMessage = 'Request to KML file timed out.';
                            break;
                        case google.maps.KmlLayerStatus.UNKNOWN:
                            errorMessage = 'Unknown error occurred while loading the KML file.';
                            break;
                        default:
                            errorMessage = `KML Layer failed to load with status: ${status}`;
                            break;
                    }
                    
                    kmlError.value = errorMessage;
                    
                    // Only log once to reduce console noise
                    if (!hasLoggedError.value) {
                        console.warn('KML Layer Warning:', errorMessage, '- The map will continue to work without the KML layer.');
                        hasLoggedError.value = true;
                    }
                    
                    // Remove the failed layer from the map to prevent further errors
                    if (kmlLayer.value) {
                        try {
                            kmlLayer.value.setMap(null);
                        } catch (e) {
                            // Ignore errors when removing the layer
                        }
                    }
                } else {
                    kmlError.value = null;
                    hasLoggedError.value = false;
                }
            });
        } catch (error) {
            kmlError.value = `Failed to create KML layer: ${error.message}`;
            if (!hasLoggedError.value) {
                console.warn('KML Layer Warning:', `Failed to create KML layer: ${error.message}`, '- The map will continue to work without the KML layer.');
                hasLoggedError.value = true;
            }
        }
    };

    // Watch for changes in the map reference
    watch(map, (newMap) => {
        if (newMap) {
            addKmlLayer(KML_LAYER_URL);
        }
    });

    return { map, kmlError };
};
